export interface RawThemeColor {
  primary?: string;
  primaryLight?: string;
  secondary?: string;
  secondaryDark?: string;
  background?: string;
  tabSecondary?: string;
  categorySegment?: string | null;
  categorySegmentBackground?: string;
  categorySelectedBackground?: string;
  categoryUnselectedBackground?: string;
  categorySelectedText?: string;
  categoryUnselectedText?: string;
  primaryTitle?: string;
  secondaryTitle?: string;
  buttonBackground?: string;
  buttonTitle?: string;
  success?: string;
  error?: string;
  [key: string]: any;
}

export interface RawThemeImages {
  backgrounds?: any[];
  brand_logo?: string;
  placeholder_logo?: string;
}

export interface RawDefaultTheme {
  theme?: {
    theme_id?: string;
    service_id?: string;
    logo_hide_title?: boolean;
    [key: string]: any;
  };
  theme_color?: RawThemeColor;
  theme_images?: RawThemeImages;
}

export interface RawLocaleEntry {
  service_id?: string;
  locale: string;
  label: string;
  description?: string;
  direction?: string;
  iso3166?: string;
  last_modified?: string;
}

export type RawStringsMap = Record<string, Record<string, string>>;

export interface RawBootstrapService {
  service_id: string;
  name: string;
  description?: string;
  is_active: boolean;
  svc_type: "platform" | "service" | string;
  iso3?: string;
  linked_id?: string;
  logo_url?: string;
  default_theme?: RawDefaultTheme;
  locales?: RawLocaleEntry[];
  strings?: RawStringsMap;
}

export interface FlatTheme {
  theme_id?: string;
  service_id?: string;
  logo_hide_title?: boolean;
  backgrounds: any[];
  brand_logo?: string;
  placeholder_logo?: string;
  [key: string]: any;
}

// --------------------------------------------------
// PLATFORM SERVICE
// --------------------------------------------------

export const findPlatformService = (
  services: RawBootstrapService[] = []
): RawBootstrapService | null =>
  services.find((service) => service?.svc_type === "platform") ?? null;

// --------------------------------------------------
// THEME
// --------------------------------------------------

export const flattenServiceTheme = (
  service: RawBootstrapService
): FlatTheme => {
  const theme = service?.default_theme?.theme ?? {};
  const color = service?.default_theme?.theme_color ?? {};
  const images = service?.default_theme?.theme_images ?? {};

  return {
    theme_id: theme.theme_id,
    service_id: service.service_id,
    logo_hide_title: theme.logo_hide_title ?? false,
    ...color,
    backgrounds: images.backgrounds ?? [],
    brand_logo: images.brand_logo,
    placeholder_logo: images.placeholder_logo,
  };
};

// Selected service ka theme → Platform theme fallback
export const resolveActiveTheme = (
  services: RawBootstrapService[] = [],
  selectedServiceId?: string | null
): FlatTheme | null => {
  const selected = selectedServiceId
    ? services.find(
        (service) => service.service_id === selectedServiceId
      )
    : null;

  if (selected) {
    return flattenServiceTheme(selected);
  }

  const platform = findPlatformService(services);

  return platform ? flattenServiceTheme(platform) : null;
};

// --------------------------------------------------
// LOCALES
// --------------------------------------------------

export const mergeLocales = (
  services: RawBootstrapService[] = []
): RawLocaleEntry[] => {
  const byLocale = new Map<string, RawLocaleEntry>();

  services.forEach((service) => {
    (service?.locales ?? []).forEach((entry) => {
      if (!entry?.locale) return;

      const key = entry.locale.toLowerCase();

      if (!byLocale.has(key)) {
        byLocale.set(key, entry);
      }
    });
  });

  return Array.from(byLocale.values());
};

// --------------------------------------------------
// STRINGS
// --------------------------------------------------

export const mergeStrings = (
  services: RawBootstrapService[] = []
): RawStringsMap => {
  const merged: RawStringsMap = {};

  services.forEach((service) => {
    const strings = service?.strings ?? {};

    Object.entries(strings).forEach(([key, localeMap]) => {
      merged[key] = {
        ...(merged[key] ?? {}),
        ...localeMap,
      };
    });
  });

  return merged;
};